import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DocumentPage } from '@/lib/api/client'
import {
  getRelativeY,
  getSubPageIndexFromAbsoluteY,
} from '@/pages/DocumentPage/components/Annotations/utils'
import { LETTER_PAGE_HEIGHT } from '@/pages/DocumentPage/components/DocumentTemplate'
import { useDocumentState } from '@/state'
import {
  Position,
  TemplateAttribute,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'

export type UseKeyboardNudgeOptions = {
  isActive: boolean
  step?: number
  fineStep?: number
  repeatDelayMs?: number
  repeatIntervalMs?: number
  onPreviewChange?: (previewDelta: Position) => void
  onCommit?: (delta: Position) => void
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
  showGridOnNudge?: boolean
  preventDefault?: boolean
  documentPage: DocumentPage
  template: TemplateAttribute<any>
  commitDebounceMs?: number
}

export type UseKeyboardNudgeReturn = {
  bind: {
    onKeyDown?: (e: React.KeyboardEvent) => void
    onKeyUp?: (e: React.KeyboardEvent) => void
  }
  previewDelta: Position
  resetPreview: () => void
}

export const useKeyboardNudge = ({
  isActive,
  step = 10,
  fineStep = 1,
  repeatDelayMs = 250,
  repeatIntervalMs = 50,
  onPreviewChange,
  onCommit,
  onNudgeEnd,
  onNudgeStart,
  showGridOnNudge = false,
  preventDefault = true,
  commitDebounceMs = 120,
  documentPage,
  template,
}: UseKeyboardNudgeOptions): UseKeyboardNudgeReturn => {
  const [preview, setPreview] = useState<Position>({ x: 0, y: 0 })
  const zoom = useDocumentState((state) => state.zoom)
  const zoomPercentage = useMemo(() => zoom / 100, [zoom])

  const heldRef = useRef<Set<string>>(new Set())
  const shiftRef = useRef(false)
  const delayRef = useRef<number | null>(null)
  const intervalRef = useRef<number | null>(null)
  const pendingDeltaRef = useRef<Position>({ x: 0, y: 0 })
  const commitTimeoutRef = useRef<number | null>(null)
  const inFlightCommitRef = useRef<Promise<void> | null>(null)

  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId: documentPage?.id as string,
  })

  const stopTimers = useCallback(() => {
    if (delayRef.current !== null) {
      window.clearTimeout(delayRef.current)
      delayRef.current = null
    }
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const cancelCommitTimeout = useCallback(() => {
    if (commitTimeoutRef.current !== null) {
      window.clearTimeout(commitTimeoutRef.current)
      commitTimeoutRef.current = null
    }
  }, [])

  const resetPreview = useCallback(() => {
    stopTimers()
    cancelCommitTimeout()
    pendingDeltaRef.current = { x: 0, y: 0 }
    heldRef.current.clear()
    setPreview({ x: 0, y: 0 })
    onPreviewChange?.({ x: 0, y: 0 })
  }, [onPreviewChange, stopTimers, cancelCommitTimeout])

  const applyStep = useCallback(
    (code: string) => {
      if (!isActive) return
      const amount = shiftRef.current ? fineStep : step

      let dx = 0,
        dy = 0
      switch (code) {
        case 'ArrowLeft':
          dx = -amount
          break
        case 'ArrowRight':
          dx = amount
          break
        case 'ArrowUp':
          dy = -amount
          break
        case 'ArrowDown':
          dy = amount
          break
        default:
          return
      }

      setPreview((p) => {
        const next = { x: p.x + dx, y: p.y + dy }
        pendingDeltaRef.current = {
          x: pendingDeltaRef.current.x + dx,
          y: pendingDeltaRef.current.y + dy,
        }
        onPreviewChange?.(next)
        return next
      })
    },
    [fineStep, isActive, onPreviewChange, step],
  )

  const startRepeat = useCallback(() => {
    stopTimers()
    delayRef.current = window.setTimeout(() => {
      delayRef.current = null
      intervalRef.current = window.setInterval(() => {
        heldRef.current.forEach((code) => applyStep(code))
      }, repeatIntervalMs)
    }, repeatDelayMs) as unknown as number
  }, [applyStep, repeatDelayMs, repeatIntervalMs, stopTimers, isActive])

  const savePosition = useCallback(
    async (delta: Position) => {
      const zoomedDeltaX = delta.x / zoomPercentage
      const zoomedDeltaY = delta.y / zoomPercentage
      const currentPosition = (template?.template_values?.position as any) || {
        x: 0,
        y: 0,
      }
      const currentX = currentPosition?.x ?? 0
      const currentY = currentPosition?.y ?? 0
      const documentSubPages =
        (documentPage?.template_values?.subPages as Array<string>) ?? []
      const currentSubPageId = template?.template_values?.subPageId ?? null
      const currentSubPageIndex = Math.max(
        documentSubPages.findIndex((id) => id === currentSubPageId),
        0,
      )
      const absoluteY =
        (currentSubPageIndex === 0
          ? currentY
          : LETTER_PAGE_HEIGHT -
            64 +
            (currentSubPageIndex - 1) * LETTER_PAGE_HEIGHT +
            currentY) + zoomedDeltaY

      const newSubPageIndex = getSubPageIndexFromAbsoluteY(absoluteY)
      const newSubPageId = documentSubPages[newSubPageIndex] ?? currentSubPageId
      const newRelativeY = getRelativeY(absoluteY)

      const updatedTemplate = {
        data_type: template.data_type,
        payload: {
          subPageId: newSubPageId,
          position: {
            ...((template?.template_values as any)?.position || {}),
            x: currentX + zoomedDeltaX,
            y: newRelativeY,
          },
        },
        templateAttributeId: template.id as string,
      }

      await updateTemplate(updatedTemplate)
    },
    [template],
  )

  const commit = useCallback(async () => {
    stopTimers()
    cancelCommitTimeout()

    const delta = {
      x: pendingDeltaRef.current.x,
      y: pendingDeltaRef.current.y,
    }

    if (delta.x === 0 && delta.y === 0) {
      resetPreview()
      return
    }

    const run = async () => {
      onCommit?.(delta)
      await savePosition(delta)
    }

    if (inFlightCommitRef.current) {
      inFlightCommitRef.current = inFlightCommitRef.current.then(run)
    } else {
      inFlightCommitRef.current = run()
    }

    await inFlightCommitRef.current
    inFlightCommitRef.current = null

    pendingDeltaRef.current = { x: 0, y: 0 }
    resetPreview()
  }, [onCommit, resetPreview, savePosition, stopTimers, cancelCommitTimeout])

  const scheduleCommit = useCallback(() => {
    cancelCommitTimeout()
    commitTimeoutRef.current = window.setTimeout(() => {
      commitTimeoutRef.current = null
      void (async () => {
        await commit()
      })()
    }, commitDebounceMs) as unknown as number
  }, [commit, commitDebounceMs, cancelCommitTimeout])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isActive) return

      const code = e.code
      if (!code.startsWith('Arrow')) return

      if ((e as unknown as KeyboardEvent).repeat) {
        if (preventDefault) e.preventDefault()
        e.stopPropagation()
        return
      }

      if (preventDefault) e.preventDefault()
      cancelCommitTimeout()
      onNudgeStart?.(showGridOnNudge)
      e.stopPropagation()

      shiftRef.current = e.shiftKey

      const firstArrow = heldRef.current.size === 0
      heldRef.current.add(code)

      applyStep(code)

      if (firstArrow) startRepeat()
    },
    [
      applyStep,
      isActive,
      preventDefault,
      startRepeat,
      cancelCommitTimeout,
      showGridOnNudge,
      onNudgeStart,
    ],
  )

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isActive) return

      const code = e.code

      if (code.startsWith('Arrow')) {
        if (preventDefault) e.preventDefault()
        e.stopPropagation()
        if (heldRef.current.size === 1) {
          onNudgeEnd?.(showGridOnNudge)
        }

        heldRef.current.delete(code)

        if (heldRef.current.size === 0) {
          scheduleCommit()
        }
      } else if (code === 'Escape') {
        if (preventDefault) e.preventDefault()
        e.stopPropagation()
        onNudgeEnd?.(showGridOnNudge)
        resetPreview()
      }
    },
    [
      isActive,
      preventDefault,
      resetPreview,
      onNudgeEnd,
      showGridOnNudge,
      scheduleCommit,
    ],
  )

  useEffect(() => {
    if (!isActive) {
      pendingDeltaRef.current = { x: 0, y: 0 }
      resetPreview()
      onNudgeEnd?.(showGridOnNudge)
    }
  }, [isActive, resetPreview, onNudgeEnd, showGridOnNudge])

  useEffect(
    () => () => {
      stopTimers()
      cancelCommitTimeout()
    },
    [stopTimers, cancelCommitTimeout],
  )

  return {
    bind: isActive ? { onKeyDown, onKeyUp } : {},
    previewDelta: preview,
    resetPreview,
  }
}
